import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { formatPrice } from '../utils/helpers';
import { initiateRazorpayPayment } from '../utils/razorpay';
import axios from 'axios';
import { Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, userId } = useApp();
  const [cartProducts, setCartProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', pincode: '' });

  useEffect(() => {
    if (cart.items.length === 0) { navigate('/cart'); return; }
    const fetch = async () => {
      try {
        setLoading(true);
        const responses = await Promise.all(cart.items.map(item => axios.get(`${API}/products/${item.product_id}`)));
        setCartProducts(responses.map((res, i) => ({ ...res.data, quantity: cart.items[i].quantity })));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [cart.items, navigate]);

  const calculateTotal = () => {
    const sub = cartProducts.reduce((t, p) => t + p.price * p.quantity, 0);
    return sub + (sub >= 15000 ? 0 : 500);
  };

  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    const orderData = {
      amount: calculateTotal(), currency: 'INR', user_id: userId,
      items: cartProducts.map(p => ({ product_id: p.id, product_name: p.name, quantity: p.quantity, price: p.price })),
      shipping_address: formData
    };
    initiateRazorpayPayment(orderData, (id, num) => { setOrderSuccess(true); setOrderNumber(num); setProcessing(false); }, () => { alert('Payment failed.'); setProcessing(false); });
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FEFCF9] flex items-center justify-center">
      <div className="inline-block w-10 h-10 border-2 border-[#E8DCC8] border-t-[#C4A265] rounded-full animate-spin"></div>
    </div>
  );

  if (orderSuccess) return (
    <div className="min-h-screen bg-[#FEFCF9] flex items-center justify-center py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-12 text-center">
          <div className="w-20 h-20 bg-[#F5EFE6] border border-[#E8DCC8] rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-[#C4A265]" /></div>
          <h1 className="text-3xl font-serif text-[#3E2723] mb-4">Order Placed Successfully!</h1>
          <p className="text-lg text-[#7A6B5D] mb-2">Order Number: <span className="font-semibold text-[#C4A265]">{orderNumber}</span></p>
          <p className="text-[#7A6B5D] mb-8">Thank you for your purchase.</p>
          <button onClick={() => navigate('/')} className="bg-[#3E2723] text-[#FEFCF9] px-8 py-3 font-semibold hover:bg-[#C4A265] transition-colors tracking-wider uppercase text-sm">Continue Shopping</button>
        </div>
      </div>
    </div>
  );

  const fields = [
    { label: 'Full Name', name: 'name', type: 'text', full: true },
    { label: 'Email', name: 'email', type: 'email' },
    { label: 'Phone', name: 'phone', type: 'tel' },
    { label: 'Address', name: 'address', type: 'textarea', full: true },
    { label: 'City', name: 'city', type: 'text' },
    { label: 'State', name: 'state', type: 'text' },
    { label: 'Pincode', name: 'pincode', type: 'text' }
  ];

  return (
    <div className="min-h-screen bg-[#FEFCF9] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-serif text-[#3E2723] mb-2">Checkout</h1>
        <div className="w-12 h-px bg-[#C4A265] mb-8"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-[#FEFCF9] border border-[#E8DCC8] p-8">
              <h2 className="text-2xl font-serif text-[#3E2723] mb-6">Shipping Information</h2>
              <div className="space-y-4">
                {fields.map(f => (
                  <div key={f.name} className={f.full ? '' : 'inline-block w-full'}>
                    <label className="block text-sm font-medium text-[#3E2723] mb-1 tracking-wider uppercase">{f.label} *</label>
                    {f.type === 'textarea' ? (
                      <textarea name={f.name} value={formData[f.name]} onChange={handleInputChange} required rows={3} className="w-full px-4 py-3 border border-[#E8DCC8] bg-white focus:border-[#C4A265] focus:outline-none" data-testid={`${f.name}-input`} />
                    ) : (
                      <input type={f.type} name={f.name} value={formData[f.name]} onChange={handleInputChange} required className="w-full px-4 py-3 border border-[#E8DCC8] bg-white focus:border-[#C4A265] focus:outline-none" data-testid={`${f.name}-input`} />
                    )}
                  </div>
                ))}
              </div>
              <button type="submit" disabled={processing} className="w-full mt-8 bg-[#3E2723] text-[#FEFCF9] py-3.5 font-semibold hover:bg-[#C4A265] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors tracking-wider uppercase text-sm" data-testid="place-order-button">
                {processing ? 'Processing...' : 'Place Order'}
              </button>
            </form>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-6 sticky top-24">
              <h2 className="text-2xl font-serif text-[#3E2723] mb-6">Order Summary</h2>
              <div className="space-y-3 mb-6">
                {cartProducts.map(p => (
                  <div key={p.id} className="flex justify-between text-sm"><span className="text-[#7A6B5D]">{p.name} x {p.quantity}</span><span className="text-[#3E2723] font-medium">{formatPrice(p.price * p.quantity)}</span></div>
                ))}
              </div>
              <div className="border-t border-[#E8DCC8] pt-4 space-y-2 mb-4">
                <div className="flex justify-between text-[#7A6B5D]"><span>Subtotal</span><span>{formatPrice(cartProducts.reduce((t, p) => t + p.price * p.quantity, 0))}</span></div>
                <div className="flex justify-between text-[#7A6B5D]"><span>Shipping</span><span>{cartProducts.reduce((t, p) => t + p.price * p.quantity, 0) >= 15000 ? 'FREE' : formatPrice(500)}</span></div>
              </div>
              <div className="border-t border-[#E8DCC8] pt-4"><div className="flex justify-between text-xl font-bold text-[#3E2723]"><span>Total</span><span>{formatPrice(calculateTotal())}</span></div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
